import { useQueries } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { queryKeys } from '@/lib/api/keys';
import { fetchProduct } from '@/lib/catalog';
import { interpolate } from '@/lib/i18n';
import type { ContactCopy } from './content';

export interface SelectedStonesProps {
  slugs: readonly string[];
  onChange: (slugs: string[]) => void;
  copy: ContactCopy['form'];
}

/**
 * The stone(s) this enquiry was started from — the product page's Enquiry button, the visualizer or a shared link
 * (`?product=<slug>`) — as removable chips (names resolved from the catalogue). The form no longer has a picker to add
 * stones (D-327): this only shows what the visitor arrived with, and lets them drop it. Nothing renders without one.
 */
export function SelectedStones({ slugs, onChange, copy }: SelectedStonesProps) {
  const names = useQueries({
    queries: slugs.map((slug) => ({
      queryKey: queryKeys.catalog.product(slug),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchProduct(slug, signal),
      staleTime: 5 * 60_000,
    })),
  });
  if (slugs.length === 0) return null;

  return (
    <div className="stones">
      <p className="label" id="enquiry-stones-label">
        {copy.aboutStone}
      </p>
      <ul className="stones__list" aria-labelledby="enquiry-stones-label">
        {slugs.map((slug, index) => {
          const product = names[index]?.data;
          const name = product ? product.name : slug;
          return (
            <li key={slug} className="stone-chip">
              <span>{name}</span>
              <button
                type="button"
                className="stone-chip__remove"
                aria-label={interpolate(copy.removeStone, { name })}
                onClick={() => {
                  onChange(slugs.filter((entry) => entry !== slug));
                }}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
